import type { Alert, AlertSummary, Severity, SupervisedCall } from "../types";

/**
 * Ordering and derivation for the wall.
 *
 * Kept as pure functions rather than inline in the components for two reasons: this is the
 * logic most likely to be wrong in a way nobody notices, and the dashboard has no DOM
 * testing library installed -- so a pure module is the only part of the console that can
 * actually be tested today.
 */

export const SEVERITY_RANK: Record<string, number> = { critical: 0, warn: 1, info: 2 };

export function severityRank(severity?: Severity | null): number {
  return SEVERITY_RANK[String(severity ?? "")] ?? 9;
}

export function summaryOf(call: SupervisedCall): AlertSummary {
  return call.alerts ?? { open: 0, acknowledged: 0, overdue: 0, total: 0, top: null };
}

/** The severity that drives a tile's colour: acknowledged alerts stop shouting. */
export function displaySeverity(call: SupervisedCall): Severity | null {
  return call.severity_open ?? null;
}

/** The same colour priority used by the wall: red, amber, watch, then quiet. */
export function callColorRank(call: SupervisedCall): number {
  const severity = displaySeverity(call);
  if (severity === "critical" || call.risk?.level === "red") return 0;
  if (severity === "warn" || call.risk?.level === "amber") return 1;
  if (severity === "info" || Number(call.risk?.score ?? 0) > 0) return 2;
  return 3;
}

export function isOverdue(call: SupervisedCall): boolean {
  return summaryOf(call).overdue > 0;
}

/**
 * A key that changes only when a call's *position* should change.
 *
 * The wall re-sorts on this rather than on the call object, because a transcript line
 * arriving every few seconds must not reshuffle twenty-five tiles under the supervisor's
 * pointer. There is no layout animation in this app -- reorders are instant -- so a tile
 * that moves while being clicked is a misclick, not a wobble.
 */
export function orderingKey(calls: SupervisedCall[]): string {
  return calls
    .map((call) => {
      const summary = summaryOf(call);
      return `${call.call_id}:${summary.overdue}:${callColorRank(call)}`;
    })
    .join("|");
}

/**
 * Most-wants-attention first: severity, then overdue, then longest-running.
 *
 * `pinnedCallId` holds one call in the position it already occupies. Without it, focusing a
 * call and then acting on it is a race against the next re-sort.
 */
export function sortCalls(
  calls: SupervisedCall[],
  options: { pinnedCallId?: string | null } = {},
): SupervisedCall[] {
  const sorted = [...calls].sort((a, b) => {
    const colour = callColorRank(a) - callColorRank(b);
    if (colour !== 0) return colour;

    const overdue = summaryOf(b).overdue - summaryOf(a).overdue;
    if (overdue !== 0) return overdue;

    // Oldest activity first inside a tier: a call that has gone quiet is the one that has
    // been waiting, which is precisely the case recency-sorting used to bury.
    const open = summaryOf(a).open + summaryOf(b).open;
    if (open > 0) {
      const aSeen = a.alerts?.top?.first_seen_at ?? "";
      const bSeen = b.alerts?.top?.first_seen_at ?? "";
      if (aSeen !== bSeen) return String(aSeen).localeCompare(String(bSeen));
    }

    const aStarted = a.created_at ? Date.parse(a.created_at) : Number.POSITIVE_INFINITY;
    const bStarted = b.created_at ? Date.parse(b.created_at) : Number.POSITIVE_INFINITY;
    return (Number.isNaN(aStarted) ? Number.POSITIVE_INFINITY : aStarted) -
      (Number.isNaN(bStarted) ? Number.POSITIVE_INFINITY : bStarted);
  });

  const pinned = options.pinnedCallId;
  if (!pinned) return sorted;

  const from = sorted.findIndex((call) => call.call_id === pinned);
  const to = calls.findIndex((call) => call.call_id === pinned);
  if (from === -1 || to === -1 || from === to) return sorted;

  // Put the focused tile back where the supervisor last saw it, without disturbing the
  // relative order of everything else.
  const [tile] = sorted.splice(from, 1);
  sorted.splice(Math.min(to, sorted.length), 0, tile);
  return sorted;
}

export interface WallCounts {
  live: number;
  critical: number;
  warning: number;
  overdue: number;
  acknowledged: number;
}

export function countCalls(calls: SupervisedCall[]): WallCounts {
  const counts: WallCounts = { live: calls.length, critical: 0, warning: 0, overdue: 0, acknowledged: 0 };
  for (const call of calls) {
    const summary = summaryOf(call);
    counts.overdue += summary.overdue;
    counts.acknowledged += summary.acknowledged;
    const colour = callColorRank(call);
    if (colour === 0) counts.critical += 1;
    else if (colour === 1) counts.warning += 1;
  }
  return counts;
}

/**
 * How loaded this supervisor is right now, 0..1+.
 *
 * Deliberately *this supervisor*, not the org. Org-wide headroom needs a presence set that
 * does not exist, and inventing one from the roster would produce a confident number that
 * is simply wrong. Overdue alerts count double because they represent attention that has
 * already been missed once.
 */
export function attentionLoad(counts: WallCounts, comfortable: number): number {
  if (comfortable <= 0) return 0;
  return (counts.critical + counts.overdue * 2) / comfortable;
}

/** Every actionable alert across the roster, for the queue view. */
export function actionableAlerts(
  calls: SupervisedCall[],
): Array<{ call: SupervisedCall; alert: Alert }> {
  const rows: Array<{ call: SupervisedCall; alert: Alert }> = [];
  for (const call of calls) {
    const top = summaryOf(call).top;
    if (top) rows.push({ call, alert: top });
  }
  return rows.sort((a, b) => {
    if (a.alert.overdue !== b.alert.overdue) return a.alert.overdue ? -1 : 1;
    const severity = severityRank(a.alert.severity) - severityRank(b.alert.severity);
    if (severity !== 0) return severity;
    return String(a.alert.first_seen_at ?? "").localeCompare(String(b.alert.first_seen_at ?? ""));
  });
}
